//From api
export interface TransactionRaw {
    id: number | undefined; //for edits
    buy: number;
    sell: number;
    fee: number;
    buyCoin: string;
    buyTicker: string;
    sellCoin: string;
    sellTicker: string;
    feeCoin: string;
    feeTicker: string;

    buyValueInFiat: number;
    sellValueInFiat: number;
    buyValueInBtc: number;
    sellValueInBtc: number;

    date: number;
    typeId: TransactionTypeValue;
    account: string;
    platformIdentifier: string;
    comment: string;

    type: TransactionType;

    missingCostBasisAmount?: number;
    assetValueMissingType?: AssetValueMissingType;

    buyPoolName?: string;
    contractAddress?: string; //buyPoolName and contractAddress share a field in the db, this is to make clear from the UI which one we are intending to use
    sellPoolName?: string;

    spamCoin?: SpamStatus;
    blacklistId?: number;

    other?: TransactionOther;

    platformImage?: string;
    buyImage?: string;
    sellImage?: string;
    feeImage?: string;
}

//With enriched data from transformer
export interface Transaction extends TransactionRaw {
    accountIncoming?: string;
    issueTypes: TransactionIssueType[];
    buyPricePerUnit?: number;
    sellPricePerUnit?: number;

    feePricePerUnit?: number;
    feeValueInFiat?: number;
}

export interface TransactionOther {
    hash?: string;
    wallet?: string;
    matched_transaction_id?: number;
    is_strict_match?: boolean;
}

export interface SelectableTransaction extends Transaction {
    isSelected: boolean;
}

export interface TransactionGroup {
    matched_transaction: Transaction;
    possible_matches: Transaction[];
    transaction: Transaction;
}

export interface TransactionsFilterData {
    search?: string;
    types?: TransactionTypeValue[];
    accounts?: string[];
    coins?: string[];
    startDate?: string;
    endDate?: string;
    reviewTypes?: string[];
}

export interface TransactionType {
    value: number;
    name: string;
    group: string;
    subgroup: string;
    subgroupName: string;
    in: boolean;
    out: boolean;
    visible?: boolean;
}

export interface Coin {
    name: string;
    ticker: string;
    image?: string;
    active: boolean;
}

export interface CoinRaw {
    coin: string;
    coinImage?: string;
    coinName: string;
}

export interface CoinWithSymbol extends Coin {
    symbol: string;
}

export interface RecalculateData {
    run: boolean; // Is the recalc running
    new: boolean; // Should the recalc be started
    info: string;
    alive: number;
    percent: number;
    uPercent: number;
}

export interface TransactionIssue {
    id: string;
    description: string;
}

export interface TransactionIssueCount {
    missingAssetValueCount: number;
    costBasisCount: number;
    spamCoinCount: number;
}

export enum TransactionIssueType {
    MissingCostBasis = '2',
    MissingPrice = '1',
    MatchedTransfer = '3',
    SpamCoin = '5',
    NoIssue = '0',
}

export interface TransactionResolutionData {
    type: 'add_price' | 'add_cost_basis' | 'link_transfer';
    data: {
        buyPrice?: number;
        sellPrice?: number;
        totalValue?: number;
        costBasis?: number;
        linkedTransactionId?: number;
    };
}
export interface SpamCenterCoin {
    blacklist_id: number; //if 0 it's in spam center, but not blacklist
    DT_RowId: string;
    contract_address: string;
    contract_address_url: string;
    csv: string;
    existing_trades: string;
    has_trades: boolean;
    network: string;
    original_id: string;
    spam: SpamStatus;
    ticker: string;
}

export enum SpamStatus {
    NotSpam = 0,
    PossibleSpam = 1,
    Spam = 2,
}

export enum TransactionUpdateType {
    AssetValueMissing = 'assetValueMissing',
    MissingCostBasis = 'missingCostBasis',
    UpdateMatch = 'updateMatch',
    Normal = 'normal',
}

export enum AssetValueMissingType {
    Buy = '1',
    Sell = '2',
    Both = '3',
}

export enum TransactionTypeValue {
    Trade = 0,
    InDeposit = 1,
    OutWithdrawal = 2,
    InIncome = 3,
    InMining = 4,
    InGiftTip = 5,
    OutSpend = 6,
    OutDonation = 7,
    OutGift = 8,
    OutStolen = 9,
    OutLost = 10,
    OutMarginLoss = 11,
    OutMarginFee = 12,
    OutBorrowingFee = 13,
    OutSettlementFee = 14,
    OutDerivativesLoss = 15,
    OutOtherFee = 16,
    OutProvideLiquidity = 17,
    OutReturnLpToken = 18,
    OutOtherExpenses = 40,
    OutExpenseNonTaxable = 41,
    OutAddCollateral = 42,
    OutRepayLoan = 43,
    OutLiquidation = 44,
    InAirdrop = 50,
    InStaking = 51,
    InMasternode = 52,
    InMinting = 53,
    InDividends = 54,
    InLending = 55,
    InInterest = 56,
    InRewards = 57,
    InMiningCommercial = 58,
    InMarginProfit = 59,
    InDerivativesProfit = 60,
    InLpIncome = 61,
    InAirdropNonTaxable = 93,
    InReceiveLoan = 94,
    InRemoveCollateral = 95,
    InOther = 90,
    InIncomeNonTaxable = 91,
    InOtherIncomeNonTaxable = 92,
    InRemoveLiquidity = 97,
    InReceiveLpToken = 98,
    InFork = 99,
    MarginTrade = 100,
    DerivativesFutureTrade = 101,
    SwapWrapNonTaxable = 102,

    OutVirtualWithdrawalFee = 1000,
    OutVirtualDepositFee = 1001,
    OutVirtualStake = 1002,
    InVirtualUnstake = 1003,
    OutVirtualTradeFee = 1004,
    InVirtualSwapTrade = 1005,
    OutVirtualSwapTrade = 1006,
    InVirtualLp = 1007,
    OutVirtualLp = 1008,
    InVirtualFork = 1009,
    OutVirtualFork = 1010,
    OutVirtualMarginLossDelta = 1100,
    OutVirtualDerivativeLossDelta = 1101,
    InVirtualZeroCostBasePool = 1102,

    // Fake, only for adding tx
    Transfer = 999,
}

export enum TransactionTypeGenericType {
    In = 'in',
    Out = 'out',
    Mixed = 'mixed',
    Trade = 'trade',
}
